import { useState, useEffect, useRef } from "react";
import { LeadBriefAttachments } from "@/components/LeadBriefAttachments";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { Edit } from "lucide-react";
import { Session } from "@supabase/supabase-js";
import { format, addDays, differenceInCalendarDays } from "date-fns";
import { AddCountryDialog } from "./AddCountryDialog";
import { PaymentTermsHelper } from "./PaymentTermsHelper";
import { DatePickerField } from "./DatePickerField";
import { ChangeReasonDialog } from "./ChangeReasonDialog";
import { useSaveWithAudit } from "@/hooks/useSaveWithAudit";
import { toast as sonnerToast } from "sonner";
import { ParticipantOptionsEditor } from "./ParticipantOptionsEditor";
import { useParticipantOptions } from "@/hooks/useParticipantOptions";

type Lead = {
  id: string;
  title: string;
  status?: string;
  brand_id: string | null;
  brand: string | null;
  client_name: string | null;
  client_liaison: string | null;
  client_email: string | null;
  group_reference: string | null;
  group_description: string | null;
  destination: string | null;
  countries: string[] | null;
  is_multi_country: boolean | null;
  start_date: string | null;
  end_date: string | null;
  pax_count: number | null;
  currency: string | null;
  payment_terms: string | null;
  notes: string | null;
  budget_range: string | null;
  lead_received_date: string | null;
  marketing_campaign_id?: string | null;
  introduced_by_client_id?: string | null;
};

type EditLeadDialogProps = {
  lead: Lead;
};

export function EditLeadDialog({ lead }: EditLeadDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const originalLeadRef = useRef<Lead>(lead);
  const { showReasonDialog, requestSave, confirmSave, cancelSave } = useSaveWithAudit();
  const { options: participantOpts } = useParticipantOptions(lead.id);
  const [cascadeDates, setCascadeDates] = useState(true);
  const [formData, setFormData] = useState({
    brand_id: lead.brand_id || "",
    client_name: lead.client_name || "",
    client_liaison: lead.client_liaison || "",
    client_email: lead.client_email || "",
    group_reference: lead.group_reference || "",
    group_description: lead.group_description || "",
    lead_received_date: lead.lead_received_date ? new Date(lead.lead_received_date) : new Date(),
    start_date: lead.start_date ? new Date(lead.start_date) : undefined as Date | undefined,
    end_date: lead.end_date ? new Date(lead.end_date) : undefined as Date | undefined,
    destination_type: (lead.is_multi_country ? "multiple" : "single") as "single" | "multiple",
    destination: lead.destination || "",
    countries: lead.countries || [] as string[],
    currency: lead.currency || "ZAR",
    payment_terms: lead.payment_terms || "",
    budget_range: lead.budget_range || "",
    notes: lead.notes || "",
    marketing_campaign_id: lead.marketing_campaign_id || "",
    introduced_by_client_id: lead.introduced_by_client_id || "",
    shipping_address: "",
  });

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });
  }, []);

  const { data: brands } = useQuery({
    queryKey: ["business-brands"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("business_brands")
        .select("*")
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  const { data: marketingCampaigns } = useQuery({
    queryKey: ["marketing-campaigns-active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("marketing_campaigns")
        .select("id, name, campaign_type, status")
        .neq("status", "cancelled")
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  const { data: introducerClients } = useQuery({
    queryKey: ["introducer-clients-picker"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("id, name, company")
        .order("company");
      if (error) throw error;
      return data;
    },
  });

  const { data: countryList } = useQuery({
    queryKey: ["countries"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("countries")
        .select("*")
        .order("name", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const trackChange = async (field: string, oldValue: any, newValue: any, reason: string) => {
    if (!session?.user?.id) return;
    
    await supabase.from("lead_changes").insert({
      lead_id: lead.id,
      user_id: session.user.id,
      field_changed: field,
      old_value: String(oldValue || ""),
      new_value: String(newValue || ""),
      change_source: reason,
    });
  };

  const updateMutation = useMutation({
    mutationFn: async ({ data, reason, dateDeltaDays }: { data: any; reason: string; dateDeltaDays?: number }) => {
      const { error } = await supabase
        .from("leads")
        .update(data)
        .eq("id", lead.id);

      if (error) throw error;

      // Track all changes with reason
      for (const [field, newValue] of Object.entries(data)) {
        const oldValue = originalLeadRef.current[field as keyof Lead];
        if (oldValue !== newValue) {
          await trackChange(field, oldValue, newValue, reason);
        }
      }

      // Cascade date shift to itinerary items and linked bookings (calendar events re-sync via triggers)
      if (dateDeltaDays && dateDeltaDays !== 0) {
        const shiftDate = (d: string | null) =>
          d ? format(addDays(new Date(d), dateDeltaDays), 'yyyy-MM-dd') : null;

        const { data: items } = await supabase
          .from("itinerary_items")
          .select("id, date, session_end_date")
          .eq("lead_id", lead.id);

        for (const item of items || []) {
          await supabase
            .from("itinerary_items")
            .update({
              date: shiftDate(item.date),
              session_end_date: shiftDate(item.session_end_date),
            })
            .eq("id", item.id);
        }

        const { data: linkedBookings } = await supabase
          .from("coffeebeans_bookings")
          .select("id, experience_date")
          .eq("lead_id", lead.id);

        for (const booking of linkedBookings || []) {
          if (booking.experience_date) {
            await supabase
              .from("coffeebeans_bookings")
              .update({ experience_date: shiftDate(booking.experience_date) })
              .eq("id", booking.id);
          }
        }

        if (session?.user) {
          await supabase.from("activity_log").insert({
            lead_id: lead.id,
            user_id: session.user.id,
            action: "Dates Shifted",
            details: `Travel dates shifted by ${dateDeltaDays} day(s). Itinerary items, linked bookings and calendar events updated to match.`,
          });
        }
      }

      // Log to activity log
      if (session?.user) {
        await supabase.from("activity_log").insert({
          lead_id: lead.id,
          user_id: session.user.id,
          action: "Lead Updated",
          details: `Lead details updated. Reason: ${reason}`,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead", lead.id] });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["lead-changes", lead.id] });
      queryClient.invalidateQueries({ queryKey: ["activity-log", lead.id] });
      sonnerToast.success("Changes saved and audit log recorded");
      setOpen(false);
    },
    onError: (error: any) => {
      sonnerToast.error(`Failed to save changes: ${error.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // GUARD CLAUSE - Prevent editing cancelled leads
    if (lead.status === 'cancelled') {
      sonnerToast.error("Cannot edit a cancelled lead");
      setOpen(false);
      return;
    }
    
    const selectedBrand = brands?.find(b => b.id === formData.brand_id);
    const isSnapStack = selectedBrand?.name === "SnapStack";
    
    const updateData = {
      brand_id: formData.brand_id,
      brand: selectedBrand?.name || null,
      client_name: formData.client_name,
      client_liaison: formData.client_liaison || null,
      client_email: formData.client_email || null,
      group_reference: isSnapStack ? null : (formData.group_reference || null),
      group_description: isSnapStack ? null : (formData.group_description || null),
      pax_count: participantOpts[0]?.quantity || lead.pax_count || 1,
      lead_received_date: format(formData.lead_received_date, 'yyyy-MM-dd'),
      start_date: isSnapStack ? null : (formData.start_date ? format(formData.start_date, 'yyyy-MM-dd') : null),
      end_date: isSnapStack ? null : (formData.end_date ? format(formData.end_date, 'yyyy-MM-dd') : null),
      is_multi_country: isSnapStack ? false : formData.destination_type === "multiple",
      countries: isSnapStack ? null : (formData.destination_type === "multiple" ? formData.countries : null),
      destination: isSnapStack ? formData.shipping_address : (formData.destination_type === "single" ? formData.destination : null),
      currency: formData.currency,
      payment_terms: formData.payment_terms || null,
      budget_range: formData.budget_range || null,
      notes: formData.notes || null,
      marketing_campaign_id: formData.marketing_campaign_id || null,
      introduced_by_client_id: formData.introduced_by_client_id || null,
      title: `${selectedBrand?.name || "Unknown"} - ${formData.client_name}`,
    };

    // Convert to comparable format for old data
    const originalBrand = brands?.find(b => b.id === originalLeadRef.current.brand_id);
    const oldDataComparable = {
      ...originalLeadRef.current,
      lead_received_date: originalLeadRef.current.lead_received_date ? format(new Date(originalLeadRef.current.lead_received_date), 'yyyy-MM-dd') : null,
      start_date: originalLeadRef.current.start_date ? format(new Date(originalLeadRef.current.start_date), 'yyyy-MM-dd') : null,
      end_date: originalLeadRef.current.end_date ? format(new Date(originalLeadRef.current.end_date), 'yyyy-MM-dd') : null,
      title: `${originalBrand?.name || "Unknown"} - ${originalLeadRef.current.client_name}`,
    };

    const dateDeltaDays = !isSnapStack && cascadeDates && formData.start_date && lead.start_date
      ? differenceInCalendarDays(formData.start_date, new Date(lead.start_date))
      : 0;

    // Request save with audit trail
    requestSave(
      oldDataComparable,
      updateData,
      async (reason: string) => {
        await updateMutation.mutateAsync({ data: updateData, reason, dateDeltaDays });
      }
    );
  };

  const selectedBrand = brands?.find(b => b.id === formData.brand_id);
  const isSnapStack = selectedBrand?.name === "SnapStack";
  const startDateChanged = !!(
    formData.start_date &&
    lead.start_date &&
    differenceInCalendarDays(formData.start_date, new Date(lead.start_date)) !== 0
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Edit className="h-4 w-4" />
          Edit Lead
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Lead Details</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="brand">Brand</Label>
            <Select
              value={formData.brand_id}
              onValueChange={(value) => {
                const selectedBrand = brands?.find(b => b.id === value);
                setFormData({ 
                  ...formData, 
                  brand_id: value,
                  currency: selectedBrand?.name === "SnapStack" ? "ZAR" : formData.currency,
                  destination_type: selectedBrand?.name === "SnapStack" ? "single" : formData.destination_type,
                });
              }}
            >
              <SelectTrigger id="brand">
                <SelectValue placeholder="Select a brand" />
              </SelectTrigger>
              <SelectContent>
                {brands?.map((brand) => (
                  <SelectItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="client_name">Client Name</Label>
            <Input
              id="client_name"
              value={formData.client_name}
              onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="client_liaison">Client Liaison</Label>
            <Input
              id="client_liaison"
              value={formData.client_liaison}
              onChange={(e) => setFormData({ ...formData, client_liaison: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="client_email">Client Email</Label>
            <Input
              id="client_email"
              type="email"
              value={formData.client_email}
              onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
              placeholder="Enter client email address"
            />
          </div>

          {!isSnapStack && (
            <div>
              <Label htmlFor="group_reference">Group Reference</Label>
              <Input
                id="group_reference"
                value={formData.group_reference}
                onChange={(e) => setFormData({ ...formData, group_reference: e.target.value })}
              />
            </div>
          )}

          <ParticipantOptionsEditor leadId={lead.id} isSnapStack={isSnapStack} />

          {!isSnapStack && (
            <div>
              <Label htmlFor="group_description">Description of Group</Label>
              <Textarea
                id="group_description"
                value={formData.group_description}
                onChange={(e) => setFormData({ ...formData, group_description: e.target.value })}
                rows={3}
              />
              <LeadBriefAttachments leadId={lead.id} />
            </div>
          )}

          <DatePickerField
            label="Date Lead Received"
            value={formData.lead_received_date}
            onChange={(date) => setFormData({ ...formData, lead_received_date: date || new Date() })}
            required
          />

          {!isSnapStack && (
            <div className="grid grid-cols-2 gap-4">
              <DatePickerField
                label="Travel Start Date"
                value={formData.start_date}
                onChange={(date) => setFormData({ ...formData, start_date: date })}
                defaultMonth={formData.start_date || formData.lead_received_date}
              />

              <DatePickerField
                label="Travel End Date"
                value={formData.end_date}
                onChange={(date) => setFormData({ ...formData, end_date: date })}
                defaultMonth={formData.start_date || formData.lead_received_date}
                disabled={(date) => formData.start_date ? date < formData.start_date : false}
              />
            </div>
          )}

          {!isSnapStack && startDateChanged && (
            <label className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={cascadeDates}
                onChange={(e) => setCascadeDates(e.target.checked)}
                className="mt-1 rounded border-gray-300"
              />
              <span className="text-sm text-amber-900">
                Shift itinerary items, linked bookings and calendar events by the same number of days, so everything stays aligned with the new travel dates.
              </span>
            </label>
          )}

          {isSnapStack ? (
            <div>
              <Label htmlFor="shipping_address">Shipping Address</Label>
              <Textarea
                id="shipping_address"
                value={formData.shipping_address}
                onChange={(e) => setFormData({ ...formData, shipping_address: e.target.value })}
                rows={3}
              />
            </div>
          ) : (
            <>
              <div>
                <Label>Destination Type</Label>
                <RadioGroup
                  value={formData.destination_type}
                  onValueChange={(value: "single" | "multiple") => 
                    setFormData({ ...formData, destination_type: value, destination: "", countries: [] })
                  }
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="single" id="edit-single" />
                    <Label htmlFor="edit-single" className="font-normal cursor-pointer">Single Destination</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="multiple" id="edit-multiple" />
                    <Label htmlFor="edit-multiple" className="font-normal cursor-pointer">Multiple Destinations</Label>
                  </div>
                </RadioGroup>
              </div>

              {formData.destination_type === "single" ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Select Country</Label>
                    <AddCountryDialog />
                  </div>
                  <Select
                    value={formData.destination}
                    onValueChange={(value) => setFormData({ ...formData, destination: value, countries: [value] })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countryList?.map((country) => (
                        <SelectItem key={country.id} value={country.name}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Select Countries</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {formData.countries.length} selected
                      </span>
                      <AddCountryDialog />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border rounded-md">
                    {countryList?.map((country) => (
                      <label
                        key={country.id}
                        className="flex items-center space-x-2 cursor-pointer hover:bg-muted/50 p-2 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={formData.countries.includes(country.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ 
                                ...formData, 
                                countries: [...formData.countries, country.name] 
                              });
                            } else {
                              setFormData({ 
                                ...formData, 
                                countries: formData.countries.filter(c => c !== country.name) 
                              });
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">{country.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <div>
            <Label htmlFor="currency">Currency</Label>
            <Select
              value={formData.currency}
              onValueChange={(value) => setFormData({ ...formData, currency: value })}
              disabled={isSnapStack}
            >
              <SelectTrigger id="currency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="ZAR">ZAR</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="budget_range">Budget Range</Label>
            <Input
              id="budget_range"
              value={formData.budget_range}
              onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
              placeholder="e.g., $10,000 - $15,000"
            />
          </div>

          <div>
            <Label htmlFor="marketing_campaign">Marketing Campaign</Label>
            <SearchableSelect
              options={[
                { value: "none", label: "Not attributed to a campaign" },
                ...(marketingCampaigns || []).map((c) => ({ value: c.id, label: c.name })),
              ]}
              value={formData.marketing_campaign_id || "none"}
              onValueChange={(value) => setFormData({ ...formData, marketing_campaign_id: value === "none" ? "" : value })}
              placeholder="Not attributed to a campaign"
              searchPlaceholder="Search campaigns…"
              emptyText="No campaigns found."
            />
            <p className="text-xs text-muted-foreground mt-1">
              Attribute this lead to a marketing campaign (FAM trip, ad campaign, trade show) for ROI reporting.
            </p>
          </div>

          <div>
            <Label htmlFor="introduced_by">Introduced By (Partner)</Label>
            <SearchableSelect
              options={[
                { value: "none", label: "No partner introduction" },
                ...(introducerClients || []).map((c: any) => ({
                  value: c.id,
                  label: c.company ? `${c.company} (${c.name})` : c.name,
                  keywords: [c.company, c.name].filter(Boolean).join(" "),
                })),
              ]}
              value={formData.introduced_by_client_id || "none"}
              onValueChange={(value) => setFormData({ ...formData, introduced_by_client_id: value === "none" ? "" : value })}
              placeholder="No partner introduction"
              searchPlaceholder="Search partners…"
              emptyText="No partners found."
            />
            <p className="text-xs text-muted-foreground mt-1">
              The partner who introduced this lead (e.g. BMP Africa at a trade show) — independent of the campaign or channel that generated it.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="payment_terms">Payment Terms</Label>
              <PaymentTermsHelper onSelect={(terms) => setFormData(prev => ({ ...prev, payment_terms: terms }))} />
            </div>
            <Textarea
              id="payment_terms"
              value={formData.payment_terms}
              onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
              placeholder="Enter payment terms or select from examples"
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>

        <ChangeReasonDialog
          open={showReasonDialog}
          onOpenChange={cancelSave}
          onConfirm={confirmSave}
          title="Reason for Lead Update"
          description="Please provide a reason for updating this lead. This will be recorded in the audit trail."
        />
      </DialogContent>
    </Dialog>
  );
}
